import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useNotifications } from '../../hooks/useNotifications'

const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
)

const TYPE_LABELS: Record<string, string> = {
  new_parcel:      'Nouveau colis',
  parcel_accepted: 'Commande acceptée',
  parcel_assigned: 'Colis assigné',
  parcel_picked_up:'Colis récupéré',
  parcel_delivered:'Colis livré',
  parcel_failed:   'Échec livraison',
  parcel_cancelled:'Commande annulée',
}

const timeAgo = (date: string): string => {
  const diff = Date.now() - new Date(date).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return "À l'instant"
  if (minutes < 60) return `Il y a ${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `Il y a ${hours}h`
  return `Il y a ${Math.floor(hours / 24)}j`
}

interface NotificationBellProps {
  triggerClassName?: string
}

const DELIVERY_ROUTE: Record<string, string> = {
  admin:      '/admin/livraisons',
  expediteur: '/expediteur/colis',
  livreur:    '/livreur/colis',
}

export const NotificationBell = ({ triggerClassName }: NotificationBellProps = {}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { notifications, unreadCount, liveNotif, markAsRead, markAllRead } = useNotifications()

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!liveNotif) return
    toast(`${liveNotif.title}\n${liveNotif.body}`, {
      icon: '🔔',
      duration: 5000,
      style: {
        background: '#1A1C1E',
        color: '#fff',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: '12px',
      },
    })
  }, [liveNotif])

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className={triggerClassName}
        style={triggerClassName ? undefined : {
          position: 'relative',
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: '10px',
          padding: '8px',
          cursor: 'pointer',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: -4, right: -4,
            background: '#1D9E75',
            color: '#fff',
            borderRadius: '50%',
            width: 18, height: 18,
            fontSize: 10,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'DM Sans, sans-serif',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          width: 360,
          maxHeight: 480,
          overflowY: 'auto',
          background: '#1A1C1E',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          zIndex: 1000,
          fontFamily: 'DM Sans, sans-serif',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>
              Notifications{unreadCount > 0 && (
                <span style={{ color: '#1D9E75' }}> ({unreadCount})</span>
              )}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  background: 'none', border: 'none',
                  color: '#1D9E75', cursor: 'pointer',
                  fontSize: 12, fontFamily: 'inherit',
                }}
              >
                Tout marquer lu
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>
              Aucune notification
            </div>
          ) : (
            notifications.map((notif) => {
              const deliveryId = notif.data?.deliveryId
              const base = DELIVERY_ROUTE[notif.recipientRole]
              const route = deliveryId && base ? `${base}/${deliveryId}` : null

              return (
              <div
                key={notif._id}
                onClick={() => {
                  if (!notif.read) markAsRead(notif._id)
                  if (route) { setIsOpen(false); navigate(route) }
                }}
                style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  background: notif.read ? 'transparent' : 'rgba(29,158,117,0.06)',
                  cursor: route ? 'pointer' : 'default',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{
                    fontSize: 12,
                    color: '#1D9E75',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}>
                    {TYPE_LABELS[notif.type] ?? notif.type}
                  </span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                    {timeAgo(notif.createdAt)}
                  </span>
                </div>
                <div style={{ color: '#fff', fontSize: 13, fontWeight: 500, marginBottom: 2 }}>
                  {notif.title}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, lineHeight: 1.5 }}>
                  {notif.body}
                </div>
                {!notif.read && (
                  <div style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: '#1D9E75', marginTop: 6,
                  }} />
                )}
              </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
